/* AdminCompliance — read-only audit dashboard for the
 * `adverse_action_notices` collection. Built so any state regulator or
 * CFPB request becomes a 30-second answer.
 *
 * Layout:
 *   • 4 KPI tiles (total / declines / reductions / email-sent rate)
 *   • Filters: kind, email status, search, lookback days
 *   • Table: when / kind / buyer / decision / limit Δ / reasons / email
 *   • Row click → drawer with full reasons + memo link
 *   • CSV export honors current filters
 */
import { useEffect, useMemo, useState } from "react";
import {
  Search, Loader2, ShieldAlert, Download, RefreshCw, X,
  AlertTriangle, MailCheck, MailWarning, ScrollText, Tag,
  ChevronRight, Filter as FilterIcon,
} from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { PageHeader } from "../../components/PortalShell";
import { Link } from "react-router-dom";

const KIND_OPTIONS = [
  { v: "",               l: "All notices" },
  { v: "decline",        l: "Declines" },
  { v: "reduced_limit",  l: "Reduced limits" },
];
const EMAIL_OPTIONS = [
  { v: "",       l: "Any email status" },
  { v: "sent",   l: "Email delivered" },
  { v: "failed", l: "Email failed" },
];
const DAYS_OPTIONS = [
  { v: 30,   l: "Last 30 days" },
  { v: 90,   l: "Last 90 days" },
  { v: 365,  l: "Last year" },
  { v: 3650, l: "All time" },
];

const KIND_BADGE = {
  decline:       { l: "Decline",        cls: "bg-rose-50 text-rose-700 border-rose-200" },
  reduced_limit: { l: "Reduced limit",  cls: "bg-amber-50 text-amber-800 border-amber-200" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function fmtMoney(n) {
  if (n === null || n === undefined || n === "") return "—";
  return `$${Number(n).toLocaleString()}`;
}

export default function AdminCompliance() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);
  const [kind, setKind] = useState("");
  const [emailStatus, setEmailStatus] = useState("");
  const [q, setQ] = useState("");
  const [drawer, setDrawer] = useState(null);

  const params = useMemo(() => ({
    days, ...(kind && { kind }), ...(emailStatus && { email_status: emailStatus }),
    ...(q.trim() && { q: q.trim() }),
  }), [days, kind, emailStatus, q]);

  async function load() {
    setLoading(true);
    try {
      const [list, sum] = await Promise.all([
        api.get("/admin/compliance/adverse-actions", { params: { ...params, limit: 100 } }),
        api.get("/admin/compliance/adverse-actions/summary", { params: { days } }),
      ]);
      setItems(list.data?.items || []);
      setTotal(list.data?.total || 0);
      setSummary(sum.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load compliance log");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days, kind, emailStatus]);

  // Search debounce
  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  function exportCsv() {
    const qs = new URLSearchParams(params).toString();
    const base = (api.defaults.baseURL || "").replace(/\/$/, "");
    const url = `${base}/admin/compliance/adverse-actions/export?${qs}`;
    // Browser fetches with cookie auth; backend sets attachment header.
    window.open(url, "_blank");
  }

  return (
    <div className="space-y-6" data-testid="admin-compliance-page">
      <PageHeader
        title="Compliance Center"
        kicker="Adverse-action audit · ECOA §615 / FCRA / GDPR Art 22"
        right={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} data-testid="compliance-refresh">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5"/>Refresh
            </Button>
            <Button size="sm" onClick={exportCsv} data-testid="compliance-export-csv">
              <Download className="w-3.5 h-3.5 mr-1.5"/>Export CSV
            </Button>
          </div>
        }
      />

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi
          icon={<ScrollText className="w-4 h-4"/>}
          label={`Notices · last ${summary?.window_days || days}d`}
          value={summary?.total ?? "—"}
          testid="kpi-total"
        />
        <Kpi
          icon={<AlertTriangle className="w-4 h-4 text-rose-600"/>}
          label="Declines"
          value={summary?.declines ?? "—"}
          testid="kpi-declines"
        />
        <Kpi
          icon={<ShieldAlert className="w-4 h-4 text-amber-600"/>}
          label="Reduced limits"
          value={summary?.reductions ?? "—"}
          testid="kpi-reductions"
        />
        <Kpi
          icon={<MailCheck className="w-4 h-4 text-emerald-600"/>}
          label="Email delivery"
          value={summary ? `${summary.email_sent_pct}%` : "—"}
          sub={summary ? `${summary.email_sent}/${summary.total} sent` : null}
          testid="kpi-email-rate"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40"/>
          <Input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search by buyer email, company, or ID…"
            className="pl-9"
            data-testid="compliance-search"
          />
        </div>
        <Select value={kind} onChange={setKind} options={KIND_OPTIONS} testid="compliance-filter-kind"/>
        <Select value={emailStatus} onChange={setEmailStatus} options={EMAIL_OPTIONS} testid="compliance-filter-email"/>
        <Select value={String(days)} onChange={(v) => setDays(Number(v))}
                options={DAYS_OPTIONS.map(o => ({ ...o, v: String(o.v) }))} testid="compliance-filter-days"/>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-foreground/60">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2"/>Loading audit log…
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-foreground/60">
            <ScrollText className="w-8 h-8 mx-auto mb-2 opacity-40"/>
            No adverse-action notices match these filters.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="compliance-table">
            <thead className="text-[11px] mono uppercase tracking-wider text-foreground/55 bg-secondary/40">
              <tr>
                <Th>When</Th>
                <Th>Kind</Th>
                <Th>Buyer</Th>
                <Th className="text-right">Limit Δ</Th>
                <Th>Top reason</Th>
                <Th>Email</Th>
                <Th className="w-8"/>
              </tr>
            </thead>
            <tbody>
              {items.map(n => (
                <tr key={n.id} className="border-t border-border hover:bg-secondary/40 cursor-pointer"
                    onClick={() => setDrawer(n)}
                    data-testid={`compliance-row-${n.id}`}>
                  <Td className="text-foreground/65">{fmtDate(n.created_at)}</Td>
                  <Td>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] border ${KIND_BADGE[n.kind]?.cls || ""}`}>
                      {KIND_BADGE[n.kind]?.l || n.kind}
                    </span>
                  </Td>
                  <Td>
                    <div className="font-medium">{n.buyer_company || "—"}</div>
                    <div className="text-[11px] text-foreground/55">{n.buyer_email}</div>
                  </Td>
                  <Td className="text-right tabular-nums">
                    {n.kind === "reduced_limit"
                      ? <span>{fmtMoney(n.previous_limit_usd)} → <strong>{fmtMoney(n.new_limit_usd)}</strong></span>
                      : <span className="text-foreground/55">—</span>}
                  </Td>
                  <Td className="max-w-[280px] truncate text-foreground/75">
                    {(n.reasons || [])[0] || "—"}
                  </Td>
                  <Td>
                    {n.email_sent
                      ? <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700"><MailCheck className="w-3 h-3"/>Sent</span>
                      : <span className="inline-flex items-center gap-1 text-[11px] text-rose-700"><MailWarning className="w-3 h-3"/>Failed</span>}
                  </Td>
                  <Td><ChevronRight className="w-4 h-4 text-foreground/30"/></Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="px-4 py-2.5 text-[11px] text-foreground/55 border-t border-border bg-secondary/20">
          Showing {items.length} of {total.toLocaleString()} notices
        </div>
      </div>

      {/* Drawer */}
      {drawer && (
        <Drawer notice={drawer} onClose={() => setDrawer(null)}/>
      )}
    </div>
  );
}

// ── Subcomponents ──────────────────────────────────────────────────────────
function Kpi({ icon, label, value, sub, testid }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3.5" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11px] mono uppercase tracking-wider text-foreground/55">
        {icon}{label}
      </div>
      <div className="display text-2xl mt-1.5">{value}</div>
      {sub && <div className="text-[11px] text-foreground/55 mt-0.5">{sub}</div>}
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-left px-4 py-2.5 font-medium ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
}

function Select({ value, onChange, options, testid }) {
  return (
    <div className="relative">
      <FilterIcon className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground/40 pointer-events-none"/>
      <select
        className="h-9 pl-8 pr-3 rounded-md border border-border bg-card text-sm"
        value={value} onChange={e => onChange(e.target.value)}
        data-testid={testid}
      >
        {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}

function Drawer({ notice, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <div className="bg-background w-full max-w-md h-full overflow-y-auto p-6 border-l border-border"
           onClick={e => e.stopPropagation()}
           data-testid="compliance-drawer">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[11px] mono uppercase tracking-wider text-foreground/55">Adverse-action notice</div>
            <div className="display text-xl mt-0.5">{notice.buyer_company || notice.buyer_email}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-secondary rounded" data-testid="compliance-drawer-close">
            <X className="w-4 h-4"/>
          </button>
        </div>
        <KV label="Kind">
          <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] border ${KIND_BADGE[notice.kind]?.cls || ""}`}>
            {KIND_BADGE[notice.kind]?.l || notice.kind}
          </span>
        </KV>
        <KV label="When">{fmtDate(notice.created_at)}</KV>
        <KV label="Buyer email">{notice.buyer_email}</KV>
        <KV label="Buyer ID"><code className="text-[11px]">{notice.buyer_id}</code></KV>
        <KV label="Decision">{notice.decision || "—"}</KV>
        {notice.kind === "reduced_limit" && (
          <KV label="Limit change">
            {fmtMoney(notice.previous_limit_usd)} → <strong>{fmtMoney(notice.new_limit_usd)}</strong>
          </KV>
        )}
        <div className="mt-5">
          <div className="text-[11px] mono uppercase tracking-wider text-foreground/55 mb-2">Top contributing factors</div>
          <ol className="space-y-2">
            {(notice.reasons || []).map((r, i) => (
              <li key={i} className="text-sm flex gap-2">
                <span className="text-foreground/40 w-4">{i + 1}.</span>
                <span>{r}</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="mt-5">
          <div className="text-[11px] mono uppercase tracking-wider text-foreground/55 mb-2">Email delivery</div>
          <KV label="Status">
            {notice.email_sent
              ? <span className="text-emerald-700">Delivered</span>
              : <span className="text-rose-700">Failed</span>}
          </KV>
          {notice.email_id && <KV label="Resend ID"><code className="text-[11px]">{notice.email_id}</code></KV>}
          {notice.email_error && <KV label="Error">{notice.email_error}</KV>}
        </div>
        <div className="mt-5 pt-5 border-t border-border">
          <Link to={`/admin/credit?buyer=${notice.buyer_id}`} className="text-sm underline text-foreground/70 hover:text-foreground inline-flex items-center gap-1.5">
            <Tag className="w-3 h-3"/>View buyer credit memo
          </Link>
        </div>
      </div>
    </div>
  );
}

function KV({ label, children }) {
  return (
    <div className="flex items-baseline gap-3 py-1.5 border-b border-border/50">
      <div className="text-[11px] mono uppercase tracking-wider text-foreground/55 w-28 shrink-0">{label}</div>
      <div className="text-sm">{children}</div>
    </div>
  );
}
